@switch (typesState().status) {
  @case ('loading') {
    <glxy-loading-spinner />
  }
  @case ('error') {
    <glxy-alert type="error">
      {{ i18n + '.LOAD_ERROR' | translate }}
      <button mat-button type="button" [attr.aria-label]="i18n + '.RETRY' | translate" (click)="retry()">
        {{ i18n + '.RETRY' | translate }}
      </button>
    </glxy-alert>
  }
  @case ('ready') {
    @if (disabled()) {
      <div class="read-only-hint">{{ i18n + '.READ_ONLY' | translate }}</div>
    }
    @if (rows().length === 0) {
      <div class="no-guardrails">{{ i18n + '.EMPTY' | translate }}</div>
    }
    @for (row of rows(); track row.form) {
      <div class="guardrail-row" [formGroup]="row.form">
        <div class="guardrail-content">
          <div class="guardrail-title">{{ typeLabel(row.form.controls.name.value) }}</div>
          @if (typeHint(row.form.controls.name.value); as hint) {
            <div class="guardrail-type-hint">{{ hint }}</div>
          }
          @if (row.spec; as spec) {
            <div class="guardrail-controls">
              <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control guardrail-control-wide">
                <glxy-label>{{ i18n + '.STAGE.LABEL' | translate }}</glxy-label>
                <mat-button-toggle-group
                  hideSingleSelectionIndicator
                  [value]="row.stage"
                  [disabled]="disabled()"
                  [attr.aria-label]="i18n + '.STAGE.LABEL' | translate"
                  (change)="setStage(row.form, $event.value)"
                >
                  <mat-button-toggle value="output">{{ i18n + '.STAGE.OUTPUT' | translate }}</mat-button-toggle>
                  <mat-button-toggle value="input">{{ i18n + '.STAGE.INPUT' | translate }}</mat-button-toggle>
                </mat-button-toggle-group>
                <glxy-hint>{{ stageHintKey(row.stage, row.form.controls.handling.value) | translate }}</glxy-hint>
              </glxy-form-field>

              <div class="guardrail-fields" formGroupName="fields">
                @for (field of spec.fields; track field.key) {
                  @let control = row.form.controls.fields.controls[field.key];
                  @switch (field.type) {
                    @case (FieldType.FIELD_TYPE_BOOL) {
                      <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control">
                        <mat-checkbox [formControlName]="field.key" color="primary">{{
                          fieldLabel(field.key)
                        }}</mat-checkbox>
                      </glxy-form-field>
                    }
                    @case (FieldType.FIELD_TYPE_INT) {
                      @let suffix = fieldSuffixKey(field.key);
                      <glxy-form-field
                        [bottomSpacing]="suffix ? 'default' : 'small'"
                        [suffixText]="suffix"
                        class="guardrail-control"
                      >
                        <glxy-label>{{ fieldLabel(field.key) }}</glxy-label>
                        <input
                          matInput
                          type="number"
                          [formControlName]="field.key"
                          [attr.aria-label]="fieldLabel(field.key)"
                        />
                        @if (!suffix && fieldHint(field.key); as hint) {
                          <glxy-hint>{{ hint }}</glxy-hint>
                        }
                      </glxy-form-field>
                    }
                    @case (FieldType.FIELD_TYPE_STRING_LIST) {
                      <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control guardrail-control-wide">
                        <glxy-label>{{ fieldLabel(field.key) }}</glxy-label>
                        <glxy-tags-input
                          [formControlName]="field.key"
                          [placeholder]="fieldLabel(field.key)"
                          [attr.aria-label]="fieldLabel(field.key)"
                        ></glxy-tags-input>
                        @if (fieldHint(field.key); as hint) {
                          <glxy-hint>{{ hint }}</glxy-hint>
                        }
                        @if (control?.touched && control?.errors?.['required']) {
                          <glxy-error>{{ i18n + '.REQUIRED' | translate }}</glxy-error>
                        }
                      </glxy-form-field>
                    }
                    @default {
                      <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control guardrail-control-wide">
                        <glxy-label>{{ fieldLabel(field.key) }}</glxy-label>
                        <textarea
                          matInput
                          cdkTextareaAutosize
                          cdkAutosizeMinRows="2"
                          cdkAutosizeMaxRows="8"
                          [formControlName]="field.key"
                          [attr.aria-label]="fieldLabel(field.key)"
                        ></textarea>
                        @let hint = fieldHint(field.key);
                        @if (hint || field.maxRunes > 0) {
                          <glxy-hint>
                            <div class="hint-with-count">
                              <span>{{ hint }}</span>
                              @if (field.maxRunes > 0) {
                                <span class="hint-count">{{ runes(control?.value ?? null) }}/{{ field.maxRunes }}</span>
                              }
                            </div>
                          </glxy-hint>
                        }
                        @if (control?.errors?.['maxRunes']) {
                          <glxy-error>{{ i18n + '.TOO_LONG' | translate }}</glxy-error>
                        }
                        @if (control?.touched && control?.errors?.['required']) {
                          <glxy-error>{{ i18n + '.REQUIRED' | translate }}</glxy-error>
                        }
                      </glxy-form-field>
                    }
                  }
                }
              </div>
              @if (row.form.controls.fields.touched && row.form.controls.fields.errors?.['atLeastOneOf']) {
                <div class="guardrail-error">{{ i18n + '.AT_LEAST_ONE' | translate }}</div>
              }

              @if (showHandlingSelect(spec)) {
                <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control">
                  <glxy-label>{{ i18n + '.HANDLING.LABEL' | translate }}</glxy-label>
                  <mat-select formControlName="handling" [aria-label]="i18n + '.HANDLING.LABEL' | translate">
                    @for (mode of offeredModes(spec); track mode) {
                      <mat-option [value]="mode">{{ handlingLabelKey(mode) | translate }}</mat-option>
                    }
                  </mat-select>
                </glxy-form-field>
              }

              @if (row.form.controls.handling.value === HandlingMode.HANDLING_MODE_BLOCK) {
                <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control guardrail-control-wide">
                  <glxy-label>{{ i18n + '.BLOCKED_MESSAGE' | translate }}</glxy-label>
                  <textarea
                    matInput
                    cdkTextareaAutosize
                    cdkAutosizeMinRows="1"
                    cdkAutosizeMaxRows="4"
                    formControlName="blockedMessage"
                    [attr.aria-label]="i18n + '.BLOCKED_MESSAGE' | translate"
                  ></textarea>
                  <glxy-hint>{{ i18n + '.BLOCKED_MESSAGE_HINT' | translate }}</glxy-hint>
                </glxy-form-field>
              }
              @if (row.form.controls.handling.value === HandlingMode.HANDLING_MODE_REPLACE) {
                <glxy-form-field [bottomSpacing]="'small'" class="guardrail-control guardrail-control-wide">
                  <glxy-label>{{ i18n + '.REPLACEMENT' | translate }}</glxy-label>
                  <input matInput formControlName="replacement" [attr.aria-label]="i18n + '.REPLACEMENT' | translate" />
                  <glxy-hint>{{ i18n + '.REPLACEMENT_HINT' | translate }}</glxy-hint>
                </glxy-form-field>
              }
            </div>
          } @else if (row.form.passthrough) {
            <div class="not-configurable">{{ i18n + '.NOT_CONFIGURABLE' | translate }}</div>
          } @else {
            <div class="not-configurable">{{ i18n + '.NOT_OFFERED' | translate }}</div>
          }
        </div>
        @if (!row.form.passthrough && !disabled()) {
          <button
            mat-icon-button
            type="button"
            [attr.aria-label]="i18n + '.REMOVE' | translate"
            (click)="removeGuardrail(row.form)"
          >
            <mat-icon class="delete-button">close</mat-icon>
          </button>
        }
      </div>
    }
    @if (!disabled()) {
      <glxy-form-field [bottomSpacing]="'none'" class="add-guardrail">
        <glxy-label>{{ i18n + '.ADD' | translate }}</glxy-label>
        <mat-select
          #addSelect
          [value]="null"
          [aria-label]="i18n + '.ADD' | translate"
          [placeholder]="i18n + '.ADD_PLACEHOLDER' | translate"
          (selectionChange)="onAddSelected(addSelect, $event.value)"
        >
          @for (type of addableTypes(); track type.name) {
            <mat-option [value]="type.name" [disabled]="type.atCap">
              {{ typeLabel(type.name) }}
              @if (type.atCap) {
                ({{ i18n + '.MAX_INSTANCES_HINT' | translate }})
              }
            </mat-option>
          }
        </mat-select>
      </glxy-form-field>
    }
  }
}
